import httpClient from './httpClient'
import type {
  ChatApiEnvelope,
  ChatMessage,
  ChatMessagePageResponse,
  ChatNegotiateResponse,
  ChatRoom,
  ChatRoomListResponse,
} from '../types/chat.types'

const CHAT_PREFIX = 'user-profile/api/chat'

const unwrap = <T,>(payload: ChatApiEnvelope<T>): T => payload.data

const chatService = {
  openPackageInquiry: async (packageId: string): Promise<ChatRoom> => {
    const { data } = await httpClient.post<ChatApiEnvelope<ChatRoom>>(
      `${CHAT_PREFIX}/rooms/package-inquiry`,
      { packageId },
    )
    return unwrap(data)
  },

  listRooms: async (page = 0, size = 20): Promise<ChatRoomListResponse> => {
    const { data } = await httpClient.get<ChatApiEnvelope<ChatRoomListResponse>>(
      `${CHAT_PREFIX}/rooms?page=${page}&size=${size}`,
    )
    return unwrap(data)
  },

  getRoom: async (roomId: string): Promise<ChatRoom> => {
    const { data } = await httpClient.get<ChatApiEnvelope<ChatRoom>>(
      `${CHAT_PREFIX}/rooms/${roomId}`,
    )
    return unwrap(data)
  },

  listMessages: async (
    roomId: string,
    page = 0,
    size = 50,
  ): Promise<ChatMessagePageResponse> => {
    const { data } = await httpClient.get<ChatApiEnvelope<ChatMessagePageResponse>>(
      `${CHAT_PREFIX}/rooms/${roomId}/messages?page=${page}&size=${size}`,
    )
    return unwrap(data)
  },

  sendMessage: async (roomId: string, body: string): Promise<ChatMessage> => {
    const { data } = await httpClient.post<ChatApiEnvelope<ChatMessage>>(
      `${CHAT_PREFIX}/rooms/${roomId}/messages`,
      { body },
    )
    return unwrap(data)
  },

  markRead: async (roomId: string): Promise<ChatRoom> => {
    const { data } = await httpClient.post<ChatApiEnvelope<ChatRoom>>(
      `${CHAT_PREFIX}/rooms/${roomId}/read`,
    )
    return unwrap(data)
  },

  linkOrder: async (roomId: string, orderId: number): Promise<ChatRoom> => {
    const { data } = await httpClient.post<ChatApiEnvelope<ChatRoom>>(
      `${CHAT_PREFIX}/rooms/${roomId}/link-order?orderId=${orderId}`,
    )
    return unwrap(data)
  },

  negotiateRoom: async (roomId: string): Promise<ChatNegotiateResponse> => {
    const { data } = await httpClient.post<ChatApiEnvelope<ChatNegotiateResponse>>(
      `${CHAT_PREFIX}/rooms/${roomId}/negotiate`,
    )
    return unwrap(data)
  },

  getUnreadCount: async (): Promise<number> => {
    const { data } = await httpClient.get<ChatApiEnvelope<{ unreadRoomCount: number }>>(
      `${CHAT_PREFIX}/unread-count`,
    )
    return unwrap(data).unreadRoomCount
  },

  sendPresenceHeartbeat: async (): Promise<void> => {
    await httpClient.post(`${CHAT_PREFIX}/presence/heartbeat`)
  },
}

export default chatService
